import { useState, useEffect, useRef, useCallback } from 'react';
import { contentAreaHeightPxForBrand, PAGE_PADDING_PX } from '@/constants/a4';
import { fitImageOnPage, type ImagePageFit } from '@/lib/imagePageFit';

const IMAGE_LOAD_TIMEOUT_MS = 5000;

/**
 * Tiptap applies these rules by default inside the editor. The measurement
 * container must replicate them so that element heights match the editor.
 * The placeholder SCOPE is replaced with the unique measurement class.
 */
const TIPTAP_BASE_CSS_TEMPLATE = `
  SCOPE li p { margin: 0; }
  SCOPE p:has(> img:only-child),
  SCOPE figure:has(> img:only-child) {
    position: static !important;
    float: none !important;
    clear: both;
    display: block;
    max-width: 100%;
    margin: 12px 0;
  }
  SCOPE img {
    position: static !important;
    float: none !important;
    clear: both;
    display: block !important;
    max-width: 100%;
    max-height: var(--quote-content-page-body-height);
    height: auto !important;
    object-fit: contain;
  }
`;

const SHORT_PARAGRAPH_MAX_WORDS = 28;
const SHORT_PAGE_MIN_FILL_RATIO = 0.18;
const MIN_PREVIOUS_PAGE_FILL_RATIO = 0.25;
const MAX_HEADING_GROUP_FILL_RATIO = 0.45;
const MAX_SHORT_PARAGRAPH_GROUP_FILL_RATIO = 0.3;
const MAX_SHORT_PARAGRAPH_GROUP_ITEMS = 3;
const PAGEFIT_BACKWARD_MERGE_TOLERANCE_RATIO = 0.035;
const BULLET_LIKE_PREFIX_RE = /^([•·●▪◦‣\-–—*]\s+|[0-9]+[.)]\s+)/;

type BreakStrategy = 'tiptap' | 'pageFit';

export type MeasuredChild = {
  outerHTML: string;
  effectiveHeight: number;
  marginTop: number;
  text: string;
  wordCount: number;
  isHeading: boolean;
  isParagraphLike: boolean;
  isListLike: boolean;
  isBulletLikeParagraph: boolean;
  isTableLike: boolean;
  isMediaLike: boolean;
  /** Geladen afbeelding (los of als enige inhoud van een alinea); mag krimpen. */
  image: { height: number; aspect: number } | null;
  /** Afbeelding verkleind tot deze hoogte zodat hij op de pagina past. */
  shrinkToHeight?: number;
};

export type MeasuredGroup = {
  children: MeasuredChild[];
  height: number;
  text: string;
  wordCount: number;
  startsWithHeading: boolean;
  leadingMarginTop: number;
};

function getWordCount(text: string): number {
  return text ? text.split(/\s+/).filter(Boolean).length : 0;
}

function isBulletLikeText(text: string): boolean {
  return BULLET_LIKE_PREFIX_RE.test(text);
}

function classifyMeasuredChild(child: HTMLElement): MeasuredChild {
  const rect = child.getBoundingClientRect();
  const style = window.getComputedStyle(child);
  const marginTop = parseFloat(style.marginTop) || 0;
  const marginBottom = parseFloat(style.marginBottom) || 0;
  const effectiveHeight = rect.height + marginTop + marginBottom;
  const tagName = child.tagName.toLowerCase();
  const text = child.textContent?.replace(/\s+/g, ' ').trim() ?? '';
  const wordCount = getWordCount(text);
  const isHeading = /^h[1-6]$/.test(tagName);
  const isParagraphLike = tagName === 'p' || tagName === 'blockquote';
  const isListLike = tagName === 'ul' || tagName === 'ol';
  const isBulletLikeParagraph = isParagraphLike && isBulletLikeText(text);
  const isTableLike = tagName === 'table';
  const onlyImage =
    child instanceof HTMLImageElement
      ? child
      : !text && child.children.length === 1 && child.firstElementChild instanceof HTMLImageElement
        ? child.firstElementChild
        : null;
  const image =
    onlyImage && onlyImage.naturalWidth > 0 && onlyImage.naturalHeight > 0
      ? {
          height: onlyImage.getBoundingClientRect().height,
          aspect: onlyImage.naturalWidth / onlyImage.naturalHeight,
        }
      : null;
  const isMediaLike = tagName === 'figure' || tagName === 'img' || image !== null;

  return {
    outerHTML: child.outerHTML,
    effectiveHeight,
    marginTop,
    text,
    wordCount,
    isHeading,
    isParagraphLike,
    isListLike,
    isBulletLikeParagraph,
    isTableLike,
    isMediaLike,
    image,
  };
}

export function createMeasuredGroup(children: MeasuredChild[]): MeasuredGroup {
  const text = children.map((child) => child.text).filter(Boolean).join(' ').trim();

  return {
    children,
    height: children.reduce((sum, child) => sum + child.effectiveHeight, 0),
    text,
    wordCount: getWordCount(text),
    startsWithHeading: children[0]?.isHeading ?? false,
    leadingMarginTop: children[0]?.marginTop ?? 0,
  };
}

function isShortParagraphLike(child: MeasuredChild): boolean {
  return child.isParagraphLike && child.wordCount > 0 && child.wordCount <= SHORT_PARAGRAPH_MAX_WORDS;
}

function canAppendToListGroup(
  groupChildren: MeasuredChild[],
  next: MeasuredChild,
  nextHeight: number,
  contentHeightPx: number,
): boolean {
  const hasListLikeChild = groupChildren.some((child) => child.isListLike || child.isBulletLikeParagraph);
  if (!hasListLikeChild) return false;
  if (next.isHeading || next.isTableLike || next.isMediaLike) return false;

  return (
    (next.isListLike || next.isBulletLikeParagraph || isShortParagraphLike(next)) &&
    nextHeight <= contentHeightPx * MAX_HEADING_GROUP_FILL_RATIO
  );
}

function buildSemanticGroups(children: MeasuredChild[], contentHeightPx: number): MeasuredGroup[] {
  const groups: MeasuredGroup[] = [];
  let index = 0;

  while (index < children.length) {
    const current = children[index];
    const groupChildren = [current];
    let groupHeight = current.effectiveHeight;
    index += 1;

    if (current.isHeading) {
      while (index < children.length) {
        const next = children[index];
        const nextHeight = groupHeight + next.effectiveHeight;
        const wouldExceedHeadingGroup = nextHeight > contentHeightPx * MAX_HEADING_GROUP_FILL_RATIO;

        if (next.isHeading || wouldExceedHeadingGroup) {
          break;
        }

        groupChildren.push(next);
        groupHeight = nextHeight;
        index += 1;

        if (next.isListLike || next.isTableLike || next.isMediaLike) {
          break;
        }
      }

      while (index < children.length) {
        const next = children[index];
        const nextHeight = groupHeight + next.effectiveHeight;
        if (!canAppendToListGroup(groupChildren, next, nextHeight, contentHeightPx)) {
          break;
        }

        groupChildren.push(next);
        groupHeight = nextHeight;
        index += 1;
      }

      groups.push(createMeasuredGroup(groupChildren));
      continue;
    }

    if (current.isListLike || current.isBulletLikeParagraph) {
      while (index < children.length) {
        const next = children[index];
        const nextHeight = groupHeight + next.effectiveHeight;
        if (!canAppendToListGroup(groupChildren, next, nextHeight, contentHeightPx)) {
          break;
        }

        groupChildren.push(next);
        groupHeight = nextHeight;
        index += 1;
      }
    }

    if (isShortParagraphLike(current)) {
      while (index < children.length) {
        const next = children[index];
        const nextHeight = groupHeight + next.effectiveHeight;

        if (
          next.isHeading ||
          !isShortParagraphLike(next) ||
          groupChildren.length >= MAX_SHORT_PARAGRAPH_GROUP_ITEMS ||
          nextHeight > contentHeightPx * MAX_SHORT_PARAGRAPH_GROUP_FILL_RATIO
        ) {
          break;
        }

        groupChildren.push(next);
        groupHeight = nextHeight;
        index += 1;
      }
    }

    groups.push(createMeasuredGroup(groupChildren));
  }

  return groups;
}

function fitTrailingImage(group: MeasuredGroup, available: number): ImagePageFit {
  const image = group.children[group.children.length - 1].image!;
  return fitImageOnPage(image.height, available - (group.height - image.height));
}

function shrinkTrailingImage(group: MeasuredGroup, height: number): MeasuredGroup {
  const last = group.children[group.children.length - 1];
  const shrunk: MeasuredChild = {
    ...last,
    effectiveHeight: last.effectiveHeight - (last.image!.height - height),
    shrinkToHeight: height,
  };
  return createMeasuredGroup([...group.children.slice(0, -1), shrunk]);
}

export function splitGroupsIntoPages(
  groups: MeasuredGroup[],
  contentHeightPx: number,
  strategy: BreakStrategy,
): MeasuredGroup[][] {
  if (groups.length === 0) return [];

  const pages: MeasuredGroup[][] = [[]];
  let pageIndex = 0;
  let currentHeight = 0;
  const startNewPage = () => {
    pageIndex += 1;
    pages.push([]);
    currentHeight = 0;
  };

  for (const group of groups) {
    const topEdge = currentHeight + group.leadingMarginTop;
    const wouldOverflowPageFit =
      strategy === 'pageFit' &&
      currentHeight > 0 &&
      currentHeight + group.height > contentHeightPx;
    const tiptapNewPage = strategy === 'tiptap' && topEdge >= contentHeightPx && currentHeight > 0;
    const endsWithImage = group.children[group.children.length - 1].image !== null;

    if (!endsWithImage && (tiptapNewPage || wouldOverflowPageFit)) startNewPage();

    // Een afbeelding wordt nooit door de paginarand afgeknipt (ook niet bij
    // `tiptap`): hij krimpt als dat kan, anders begint hij op de volgende pagina.
    let placed = group;
    if (endsWithImage && currentHeight + group.height > contentHeightPx) {
      let fit = fitTrailingImage(group, contentHeightPx - currentHeight);
      if (fit.kind === 'nextPage' && currentHeight > 0) {
        startNewPage();
        fit = fitTrailingImage(group, contentHeightPx);
      }
      if (fit.kind === 'nextPage' && group.children.length > 1) {
        // De tekst boven de afbeelding vult de pagina al: afbeelding apart verder.
        pages[pageIndex].push(createMeasuredGroup(group.children.slice(0, -1)));
        startNewPage();
        placed = createMeasuredGroup(group.children.slice(-1));
        fit = fitTrailingImage(placed, contentHeightPx);
      }
      if (fit.kind === 'shrink') placed = shrinkTrailingImage(placed, fit.height);
    }

    pages[pageIndex].push(placed);
    currentHeight += placed.height;
  }

  return pages;
}

function getPageHeight(groups: MeasuredGroup[]): number {
  return groups.reduce((sum, group) => sum + group.height, 0);
}

function getPageText(groups: MeasuredGroup[]): string {
  return groups.map((group) => group.text).filter(Boolean).join(' ').trim();
}

function isShortPage(groups: MeasuredGroup[], contentHeightPx: number): boolean {
  const text = getPageText(groups);
  const wordCount = getWordCount(text);
  const pageHeight = getPageHeight(groups);

  return pageHeight < contentHeightPx * SHORT_PAGE_MIN_FILL_RATIO || wordCount < 24;
}

function tryMergeShortPageBackIntoPrevious(
  previousPage: MeasuredGroup[],
  currentPage: MeasuredGroup[],
  contentHeightPx: number,
): boolean {
  if (currentPage.length === 0 || !isShortPage(currentPage, contentHeightPx)) return false;

  const currentHeight = getPageHeight(currentPage);
  const previousHeight = getPageHeight(previousPage);
  const startsWithHeading = currentPage[0]?.startsWithHeading ?? false;
  const canFitOnPreviousPage =
    previousHeight + currentHeight <= contentHeightPx * (1 + PAGEFIT_BACKWARD_MERGE_TOLERANCE_RATIO);

  if (startsWithHeading || !canFitOnPreviousPage) return false;

  previousPage.push(...currentPage);
  currentPage.length = 0;
  return true;
}

function rebalanceShortPages(pages: MeasuredGroup[][], contentHeightPx: number): MeasuredGroup[][] {
  const balancedPages = pages.map((page) => [...page]);

  for (let pageIndex = 1; pageIndex < balancedPages.length; pageIndex += 1) {
    const currentPage = balancedPages[pageIndex];
    const previousPage = balancedPages[pageIndex - 1];

    if (tryMergeShortPageBackIntoPrevious(previousPage, currentPage, contentHeightPx)) {
      continue;
    }

    while (isShortPage(currentPage, contentHeightPx) && previousPage.length > 1) {
      const candidate = previousPage[previousPage.length - 1];
      const nextCurrentHeight = getPageHeight(currentPage) + candidate.height;
      const nextPreviousHeight = getPageHeight(previousPage) - candidate.height;

      if (
        candidate.startsWithHeading ||
        nextCurrentHeight > contentHeightPx ||
        nextPreviousHeight < contentHeightPx * MIN_PREVIOUS_PAGE_FILL_RATIO
      ) {
        break;
      }

      previousPage.pop();
      currentPage.unshift(candidate);
    }
  }

  return balancedPages.filter((page) => page.length > 0);
}

function renderChild(child: MeasuredChild): string {
  if (child.shrinkToHeight === undefined || !child.image) return child.outerHTML;
  const template = document.createElement('template');
  template.innerHTML = child.outerHTML;
  const img = template.content.querySelector('img');
  if (!img) return child.outerHTML;
  // Breedte i.p.v. hoogte: een eerder ingestelde procentbreedte zou de verhouding anders vervormen.
  img.style.width = `${Math.floor(child.shrinkToHeight * child.image.aspect)}px`;
  return template.innerHTML;
}

function renderPageGroups(pages: MeasuredGroup[][]): string[] {
  return pages.map((groups) => groups.flatMap((group) => group.children.map(renderChild)).join(''));
}

function waitForImages(container: HTMLElement): Promise<void> {
  const imgs = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];
  if (imgs.length === 0) return Promise.resolve();
  return Promise.race([
    Promise.all(
      imgs.map(
        (img) =>
          img.complete
            ? Promise.resolve()
            : new Promise<void>((resolve) => {
                img.onload = () => resolve();
                img.onerror = () => resolve();
              }),
      ),
    ).then(() => {}),
    new Promise<void>((resolve) => setTimeout(resolve, IMAGE_LOAD_TIMEOUT_MS)),
  ]);
}

/**
 * The Tiptap Pages extension places a page break when the cumulative content
 * height reaches the boundary. It does NOT move the overflowing element to
 * the next page — instead the element stays on the current page and is
 * clipped by overflow:hidden on the page wrapper. The next page then
 * continues with the remaining elements.
 *
 * To replicate this behaviour we place each element on the current page as
 * long as its TOP EDGE starts before the page boundary. Only when the top
 * edge would start beyond the boundary do we move to a new page. Images are
 * the exception in both strategies: see `splitGroupsIntoPages`.
 *
 * `pageFit`: nieuwe pagina zodra het kind niet meer in de **resterende** hoogte
 * past (beter voor PDF; voorkomt dat één fragment fysiek hoger is dan A4-body).
 */
function splitChildren(
  contentDiv: HTMLElement,
  contentHeightPx: number,
  strategy: BreakStrategy = 'tiptap',
): string[] | null {
  const children = Array.from(contentDiv.children) as HTMLElement[];
  if (children.length === 0) return [];

  const measuredChildren = children.map((child) => classifyMeasuredChild(child));
  const groups = buildSemanticGroups(measuredChildren, contentHeightPx);
  const splitPages = splitGroupsIntoPages(groups, contentHeightPx, strategy);
  const rebalancedPages = strategy === 'pageFit' ? rebalanceShortPages(splitPages, contentHeightPx) : splitPages;

  const changed =
    rebalancedPages.length > 1 ||
    rebalancedPages.some((page) =>
      page.some((group) => group.children.some((child) => child.shrinkToHeight !== undefined)),
    );
  // null: één pagina zonder aanpassing, de aanroeper houdt de originele HTML.
  return changed ? renderPageGroups(rebalancedPages) : null;
}

function stripHtmlToPlainText(html: string): string {
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Voegt een te kort laatste fragment samen met het vorige zodat er geen
 * orphan-pagina ontstaat met slechts een paar regels. Drempels zijn ruim
 * genoeg om fragmenten tot ~40% van een A4-pagina samen te voegen.
 */
export function mergeShortTrailingHtmlPages(
  pages: string[],
  enabled: boolean,
  maxChars = 400,
  maxWords = 50,
): string[] {
  if (!enabled || pages.length < 2) return pages;
  const last = pages[pages.length - 1];
  // Een afbeelding staat juist apart omdat hij niet meer op de vorige pagina paste.
  if (/<img\b/i.test(last)) return pages;
  const text = stripHtmlToPlainText(last);
  const wordCount = text ? text.split(/\s+/).filter(Boolean).length : 0;
  if (text.length < maxChars || wordCount < maxWords) {
    const prev = pages[pages.length - 2];
    return [...pages.slice(0, -2), prev + last];
  }
  return pages;
}

/**
 * Splits an HTML string into multiple page-sized chunks by measuring
 * top-level element heights in a hidden container with matching styles.
 *
 * Waits for both fonts and images to load before measuring so that
 * heights are accurate.
 *
 * Returns an array of HTML strings — one per page.
 * Falls back to a single-page array when measurement fails or html is empty.
 */
export type UseContentPagesOptions = {
  /** A4-breedte in mm voor tekstomloop (default 297). */
  pageWidthMm?: number;
  /** UBX-pagina’s hebben een hogere header → kleinere contenthoogte (zelfde als PageSectionView). */
  brand?: 'ubo' | 'ubx';
  /** Na meting (inclusief lege html); bv. voor Puppeteer `data-print-layout-ready`. */
  onMeasureComplete?: () => void;
  /**
   * Publieke print/PDF: voeg kort laatste fragment samen met vorige (bijv. alleen handtekeningregel).
   */
  mergeShortTrailingFragment?: boolean;
  /**
   * `pageFit`: splits op resterende hoogte (PDF-vriendelijk). `tiptap`: gedrag gelijk aan editor Pages.
   */
  pageBreakStrategy?: 'tiptap' | 'pageFit';
};

export function useContentPages(
  html: string,
  cssText: string,
  options: UseContentPagesOptions = {},
): string[] {
  const pageWidthMm = options.pageWidthMm ?? 297;
  const brand = options.brand ?? 'ubo';
  const mergeShortTrailingFragment = options.mergeShortTrailingFragment ?? false;
  const pageBreakStrategy = options.pageBreakStrategy ?? 'tiptap';
  const contentHeightPx = contentAreaHeightPxForBrand(brand);
  const onMeasureCompleteRef = useRef(options.onMeasureComplete);
  onMeasureCompleteRef.current = options.onMeasureComplete;

  const [pages, setPages] = useState<string[]>([html]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cancelledRef = useRef(false);
  const measureRevisionRef = useRef(0);

  const measure = useCallback(async () => {
    const revision = ++measureRevisionRef.current;
    const signalComplete = () => {
      if (!cancelledRef.current && revision === measureRevisionRef.current) {
        onMeasureCompleteRef.current?.();
      }
    };

    if (!html) {
      setPages([html]);
      signalComplete();
      return;
    }

    const container = document.createElement('div');
    container.style.cssText = `
      position: absolute;
      left: -9999px;
      top: 0;
      width: ${pageWidthMm}mm;
      padding: ${PAGE_PADDING_PX}px;
      box-sizing: border-box;
      visibility: hidden;
      pointer-events: none;
    `;
    container.style.setProperty('--quote-content-page-body-height', `${contentHeightPx}px`);

    const uniqueClass = `measure-${Date.now()}-${revision}`;
    const baseStyleEl = document.createElement('style');
    baseStyleEl.textContent = TIPTAP_BASE_CSS_TEMPLATE.replace(/SCOPE/g, `.${uniqueClass}`);
    container.appendChild(baseStyleEl);

    const styleEl = document.createElement('style');
    styleEl.textContent = cssText.replace(/\.[a-zA-Z][\w-]*-readonly-[^\s{]+/g, `.${uniqueClass}`);
    container.appendChild(styleEl);

    const contentDiv = document.createElement('div');
    contentDiv.className = uniqueClass;
    contentDiv.innerHTML = html;
    container.appendChild(contentDiv);

    document.body.appendChild(container);
    containerRef.current = container;

    try {
      await waitForImages(contentDiv);

      if (cancelledRef.current || revision !== measureRevisionRef.current) return;

      let pageStrings = splitChildren(contentDiv, contentHeightPx, pageBreakStrategy) ?? [html];
      if (pageBreakStrategy !== 'pageFit') {
        pageStrings = mergeShortTrailingHtmlPages(pageStrings, mergeShortTrailingFragment);
      }

      if (cancelledRef.current || revision !== measureRevisionRef.current) return;

      setPages(pageStrings.length > 0 ? pageStrings : [html]);
      signalComplete();
    } catch {
      if (!cancelledRef.current && revision === measureRevisionRef.current) {
        setPages([html]);
        signalComplete();
      }
    } finally {
      if (container.isConnected) container.remove();
      if (containerRef.current === container) {
        containerRef.current = null;
      }
    }
  }, [html, cssText, pageWidthMm, contentHeightPx, mergeShortTrailingFragment, pageBreakStrategy]);

  useEffect(() => {
    cancelledRef.current = false;
    document.fonts.ready.then(() => {
      if (!cancelledRef.current) measure();
    });
    return () => { cancelledRef.current = true; };
  }, [measure]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      measureRevisionRef.current += 1;
      if (containerRef.current) {
        containerRef.current.remove();
        containerRef.current = null;
      }
    };
  }, []);

  return pages;
}
